import { buildFormValidate } from "@turo/libraries/lib/helpers/validationHelpers";
import { GhostButton, PrimaryButton, TextInput } from "@turo/pedal";
import type { DriverAccountResponse } from "@turo/types-dunlop";
import { StyledText } from "@turo/ui";
import { type FC, type FormEventHandler, useEffect, useState } from "react";

import { OTPButtonContainer } from "~/src/components/OTPButtonContainer";
import { OTPForm } from "~/src/components/OTPForm";
import { OtpResendPasscodeButton } from "~/src/components/OtpResendPasscodeButton";
import { OTP_METHODS } from "~/src/constants/otp";
import { onShowOtpEmailConfirmStep } from "~/src/helpers/formEventActions";
import { dispatchLoginFailure } from "~/src/helpers/loginActions";
import { getRecaptchaToken } from "~/src/helpers/recaptcha";
import { OTP_TRACKING_EVENT, trackOtp } from "~/src/helpers/tracking";
import { useSimpleTranslate } from "~/src/SimpleTranslateProvider";
import type { StateFormField } from "~/types/stateForm";

import stateForm, { FIELD_TYPES } from "../../components/stateForm";
import { generateOTP, loginWithOTP } from "../../helpers/otpHelpers";
import {
  isPasscodeEmpty,
  otpPasscodeValidate,
} from "../../helpers/passcodeFormHelpers";
import type { OtpLoginEmailTranslations } from ".";

interface EmailConfirmOTPFormComponentProps {
  email: string;
  loginStart: () => void;
  loginSuccess: (data: DriverAccountResponse) => void;
  onClickBack: () => void;
  onResendOtpTokenSuccess: (token: string) => void;
  otpToken: string;
}

interface StateFormProps {
  buildSubmitHandler: (
    onSubmit: (formData: { passcode: string }) => Promise<unknown>,
  ) => FormEventHandler<HTMLFormElement>;
  formFields: {
    passcode: StateFormField;
  };
  isLoading: boolean;
}

type EmailConfirmOTPFormProps = EmailConfirmOTPFormComponentProps &
  StateFormProps;

function EmailConfirmOTPForm({
  buildSubmitHandler,
  email,
  formFields: { passcode },
  isLoading,
  loginStart,
  loginSuccess,
  onClickBack,
  onResendOtpTokenSuccess,
  otpToken,
}: EmailConfirmOTPFormProps) {
  const translate = useSimpleTranslate<OtpLoginEmailTranslations>();
  const [formError, setFormError] = useState<string>();
  const [isResendingOtpToken, setIsResendingOtpToken] = useState(false);

  useEffect(() => {
    onShowOtpEmailConfirmStep();
    trackOtp(OTP_TRACKING_EVENT.VIEWED_CONFIRM_PASSCODE);
  }, []);

  const handleSubmitPasscode = buildSubmitHandler(async (formData) => {
    loginStart();
    trackOtp(OTP_TRACKING_EVENT.CONFIRM_LOGIN_CLICK);

    const recaptchaTokenOrError = await getRecaptchaToken({ translate });

    const data =
      typeof recaptchaTokenOrError === "string"
        ? await loginWithOTP({
            passcode: formData.passcode,
            recaptchaToken: recaptchaTokenOrError,
            token: otpToken,
          })
        : recaptchaTokenOrError;

    if ("error" in data) {
      dispatchLoginFailure(data);
      return setFormError(data.errors[0].message ?? undefined);
    }

    return loginSuccess(data);
  });

  const handleOTPTokenResend = async () => {
    setIsResendingOtpToken(true);
    trackOtp(OTP_TRACKING_EVENT.RESEND_PASSCODE_CLICK);

    const recaptchaTokenOrError = await getRecaptchaToken({ translate });

    const data =
      typeof recaptchaTokenOrError === "string"
        ? await generateOTP({
            body: {
              email,
              method: OTP_METHODS.EMAIL,
            },
            recaptchaToken: recaptchaTokenOrError,
          })
        : recaptchaTokenOrError;

    setIsResendingOtpToken(false);

    if ("error" in data) {
      dispatchLoginFailure(data);
      return setFormError(data.errors[0].message ?? undefined);
    }

    /**
     * The current behavior sends the same OTP token (and same email code).
     * Should this behavior change, this will ensure the token is not out
     * of sync with the code we send.
     */
    return onResendOtpTokenSuccess(data.token);
  };

  return (
    <OTPForm onSubmit={handleSubmitPasscode}>
      <StyledText marginBottom={0} styleName="bodyMobile">
        {`${translate(
          "auth.login.otp.email.passcode.form.helper_text",
        )} ${email}`}
      </StyledText>
      <TextInput
        id="passcode"
        label={translate("auth.login.otp.email.passcode.form.label")}
        name="passcode"
        onBlur={passcode.onBlur}
        onChange={passcode.onChange}
        onInput={passcode.onBlur}
        placeholder={translate(
          "auth.login.otp.email.passcode.form.placeholder",
        )}
        status={!!passcode.error || formError ? "error" : undefined}
        supportingText={formError}
        type={FIELD_TYPES.TEXT}
        value={passcode.value}
      />
      <OtpResendPasscodeButton
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        handleOTPTokenResend={handleOTPTokenResend}
        isResendingOtpToken={isResendingOtpToken}
        otpMethod={OTP_METHODS.EMAIL}
      />
      <OTPButtonContainer>
        <PrimaryButton
          data-testid="submitLoginButton"
          isDisabled={isPasscodeEmpty(passcode.value) || !!passcode.error}
          isLoading={isLoading}
          type="submit"
        >
          {translate("auth.login.otp.email.continue.button_label")}
        </PrimaryButton>
        <GhostButton onClick={onClickBack}>
          {translate("auth.login.otp.email.back.button_label")}
        </GhostButton>
      </OTPButtonContainer>
    </OTPForm>
  );
}

// @ts-expect-error -- TODO-TS: Convert stateForm
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Cast is necessary until we convert stateForm to TypeScript
export default stateForm(EmailConfirmOTPForm, {
  types: {
    passcode: FIELD_TYPES.NUMBER,
  },
  validate: buildFormValidate({
    passcode: otpPasscodeValidate(),
  }),
}) as FC<EmailConfirmOTPFormComponentProps>;
